<template>
  <div class="row">
    <template v-if="order && (order.product?.sku?.includes('SH') || order.product?.sku?.includes('SM'))">
      <div class="col-12 text-right">
        <router-link v-bind:to="{ name: 'DeviceTesting', query: {id_number: order.id_number } }" class="btn btn-warning btn-md">
          <i class="fa fa-tasks mr-2"></i>
          Run Devices Test</router-link> 
        </div>
    </template>
    <div class="col-12 col-md-3 pr-0">
      <div class="card">
        <div class="card-header">
          <h4 class="mb-0">
            <router-link v-bind:to="{ name: 'Production' }" class="btn btn-outline-secondary btn-md mr-4"><i class="fa fa-arrow-left"></i></router-link>
            Details
          </h4>
        </div>
        <div class="card-body" v-if="order">
          <div class="row details">
            <div class="col-12 col-md-12">
              <router-link v-if="isCompleted" v-bind:to="{ name: 'ProductionOrderCertificate', params: { id: order.id_number }}" 
                class="btn ups btn-sm mb-4" target="_blank">
                <i class="fa fa-file mr-1"></i> Download Certificate
              </router-link>
              <div>
                <strong>ID Number:</strong> {{ order.id_number }}
                <br>
                <kbd class="kbd" v-if="order.sap_document_id">{{ order.sap_document_id }}</kbd>
              </div>
              <div v-if="$router.user.roleScore > 1">
                <strong>Assembler:</strong> {{ order.assembler }}
              </div>
              <div v-if="$router.user.roleScore > 1">
                <strong>Warehouse:</strong> {{ order.warehouse }}
              </div>
              <div v-if="order.operators">
                <strong>Operators ID:</strong> {{ order.operators.join(',') }}
              </div>
              <div v-if="order.sap_id">
                <strong>SAP ID:</strong> {{ order.sap_id }}
              </div>
              <div>
                <strong>Status:</strong>
                <span class="status ml-2" :class="order.status">{{ order.status }}</span>
                <kbd class="ml-2">{{ order.typology}}</kbd>
              </div>
              <div>
                <i class="fas fa-calendar"></i> <strong>Due Date:</strong>
                {{ order.date | formatDate }}
              </div>
              <small>
                <strong>Created at:</strong>
                {{ order.createdAt | formatDate }}
              </small>
              <br>
              <small>
                <strong>Last update:</strong>
                {{ order.updatedAt | formatDate }}
              </small>
            </div>
            <div class="col-12">
              <div v-if="order.notes" class="notes">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                <strong>Notes:</strong> {{ order.notes }}
              </div>
            </div>
            <div class="col-12 col-md-12 mt-3">
              <div class="form-inline justify-content-between">
                <strong>Product:</strong>
                <div v-if="order.product" class="bullet" :style="`background-color: ${colors[order.product.commercial_code]}`"></div>
                <!-- <BOM class="mt-2" :sku="order.product.sku"></BOM> -->
              </div>
              <table class="product">
                <tr v-for="(value, key, index) in order.product" :key="index" :class="key">
                  <td><b>{{ key | label }}:</b></td>
                  <td>{{ value }}</td>
                </tr>
              </table>
            </div>
          </div>
          <div class="row mt-3" v-if="order.typology === 'production' || isReworkOrder">
            <div class="col-12 text-center" v-if="!order.product_serials || !order.product_serials.length">
              <button class="btn btn-secondary btn-block" @click="startProduction()">
                <i class="fa fa-barcode mr-2"></i>
                <span>Generate Serials</span>
              </button>
            </div>
            <template v-else>
              <div class="col-12">
                <download-csv class="btn print" delimiter=";" :data="labelsCsvData" :name="order.id_number + '_Labels.csv'">
                  <div class="icons">
                    <i class="fa fa-2x fa-file-excel"></i><i class="fa fa-arrow-right"></i><i class="fa fa-2x fa-print"></i>
                  </div>
                  Download & Print Labels
                </download-csv>
              </div>
              <div class="col-12">
                <label>Main Product Serials</label>
                <ul>
                  <li v-for="srl in order.product_serials" :key="srl">
                    <span :class="{strike: order.serials.find((serial) => serial['product'] === srl) || order.nc_products.find((serial) => serial['product'] === srl)}">{{ srl }}</span>
                  </li>
                </ul>
                <button v-if="$router.user.roleScore >= 2" class="btn btn-sm btn-light" @click="copySerialsToClipboard()"><i class="fa fa-copy"></i> Copy serials to clipboard</button>
              </div>
              <div class="col-12" v-if="order.product_2_serials && order.product_2_serials.length">
                <label>Secondary Product Serials</label>
                <ul>
                  <li v-for="srl in order.product_2_serials" :key="srl">
                    <span :class="{strike: order.serials.find((serial) => serial['product_2'] === srl) || order.nc_products.find((serial) => serial['product_2'] === srl)}">{{ srl }}</span>
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 col-md-9" v-if="order && order.product">
      <div class="card">
        <div class="grid_row serials serials__heading" :style="`grid-template-columns: repeat(${visibleFieldsCount},${100/visibleFieldsCount}%)`">
          <div class="col">
            <button class="btn btn-sm ml-2" @click="enableEditing = !enableEditing" title="Enable editing"><i class="fa fa-edit"></i></button>
            <label>#</label>
          </div>
          <div v-if="isReworkOrder">Ex Serial</div>
          <template v-for="(field) in productFields">
            <div v-if="field.type !== 'AUTO'" :key="field.name" class="thead" :class="{'batch': field.batch && !enableEditing}">
              <div class="d-inline-flex align-items-center">
                <button v-if="field.batch" class="btn btn-sm ml-2" @click="batchScan(field.key)" title="Batch Scan"><i class="fa fa-barcode"></i></button>
                <label>{{ field.name }}</label>
              </div>
            </div>
          </template>
          <template v-if="isDisassemblyOrder">
            <div v-if="disassemblyNotes.length" class="d-inline-flex justify-content-center align-items-center">
              <span>Dettagli Smontaggio</span>
            </div>
          </template>
          <template v-else-if="!isShelfy">
            <div v-if="discardReasons.length" class="d-inline-flex justify-content-center align-items-center">
              <span>Segnalazione NC</span>
              <a class="ml-2 btn btn-sm text-white" href="#nc-products"><i class="fa fa-angle-down"></i></a>
            </div>
          </template>
          <template>
            <div class="d-inline-flex justify-content-center align-items-center">
              <span>Quality Check</span>
              <span class="ml-2 px-1 rounded" :class="order.quality_check_passed_percentage < 2 ? 'bg-danger' : 'bg-success'">{{ order.quality_check_count }}</span>
            </div>
          </template>
        </div>
        <!-- Serials rendering -->
        <div v-for="(serial, index) in order.serials" :key="index" class="serials grid_row" :class="{'selected': serial.product === selectedSerial, 'recently-saved': recentlySavedRows.has(index), 'recently-completed': recentlyCompletedRows.has(index) }"
          :style="`grid-template-columns: repeat(${visibleFieldsCount},${100/visibleFieldsCount}%)`">
          <div>
            <strong class="col">{{ index + 1 }}</strong>
            <small class="text-nowrap">{{ serial.timestamp | formatTimestamp }}</small>
          </div>
          <template v-if="isReworkOrder">
            <input v-model.trim="serial.ex_serial" :key="index+'-ex_serial'"
              :class="{ ok: serialsCheck[index]['ex_serial'], error: !serialsCheck[index]['ex_serial'] && serialsCheck[index]['ex_serial'] != null }"
              @focus="onInputFocus('ex_serial', index)"
              @blur="onInputBlur(), serialsInputController(serial, index, 'ex_serial')"
              @input="onInputChange('ex_serial', index)"
              :readonly="isLoading" class="col" type="text"
            />
          </template>

          <template v-for="(field) in productFields">
            <input v-model.trim="serial[field.key]"
              :key="index+field.key"
              :disabled="enableEditing && field.type === 'DISABLED' || (field.batch && !enableEditing) || (serialsCheck[index][field.key] && !enableEditing)"
              :required="field.type === 'REQUIRED'" :hidden="field.type === 'AUTO'"
              :class="{ ok: serialsCheck[index][field.key], error: !serialsCheck[index][field.key] && serialsCheck[index][field.key] != null }"
              @focus="onInputFocus(field.key, index), activeSerialIndexCheck(index), $event.target.select()" 
              @blur="onInputBlur(), serialsInputController(serial, index, field.key)"
              @input="onInputChange(field.key, index)"
              :readonly="isLoading || (order.sap_status === 'closed' && !enableEditing)" class="col" type="text"
            />
          </template>      
          <template v-if="isDisassemblyOrder">    
            <div class="select" v-if="disassemblyNotes.length">
              <select class="select-info form-control form-control-sm ml-1" tabindex="-1" v-model="serial.notes">
                <option disabled selected value="">Seleziona dettagli smontaggio</option>
                <option v-for="item in disassemblyNotes" :key="item">{{ item }}</option>
              </select>
            </div>
          </template>
          <template v-else-if="!isShelfy">    
            <div class="select" v-if="discardReasons.length">
              <select class="select-danger form-control form-control-sm ml-1" tabindex="-1" @change="discard(serial, index, $event.target.value), $event.target.value = false">
                <option disabled selected :value="false">Segnala NC</option>
                <optgroup v-for="c in discardReasons" :key="c.category" :label="c.category">
                  <option v-for="v in c.values" :key="v.name" :value="JSON.stringify({id: v.id, value: v.name, category: c.category, index: c.index})">{{ v.name }}</option>
                </optgroup>
              </select>
            </div>
          </template>
          <div v-if="order.status === 'completed'">
            <div v-if="serial.quality_check">
              <div class="mx-2 px-1 rounded" :class="serial.quality_check.is_passed ? 'bg-success' : 'bg-danger'" @click="showQualityCheckNotes(serial.quality_check.notes)">
                {{ serial.quality_check.is_passed ? 'OK' : 'KO' }}
                <span v-if="serial.quality_check.notes && serial.quality_check.notes.length" :title="serial.quality_check.notes" class="ml-2 fa fa-info"></span>
              </div>
              <small>{{ serial.quality_check.timestamp | formatTimestamp }}</small>
            </div>
            <button v-else-if="serial.product" class="btn btn-sm" @click="startQualityCheck(serial.product)">Start Quality Check</button>
          </div>
        </div>
        <input type="text" placeholder="" @focus="activeSerialIndexCheck(order.serials.length)" />
        <button type="submit" class="btn btn-md btn-outline-secondary btn-block mt-4" :class="{ loading: isLoading || isSaving }" @click.prevent="manualSave()">
          <i class="fa" :class="isSaving ? 'fa-spinner fa-spin' : 'fa-save'"></i> 
          {{ isSaving ? 'Saving...' : 'Save' }}
          <span v-if="pendingChanges.size > 0 && !isSaving" class="badge badge-warning ml-2" 
                :title="`${pendingChanges.size} row(s) with unsaved changes`">{{ pendingChanges.size }}</span>
        </button>
      </div>
      <Modal ref="qualityCheck" title="Quality Check">
        <template #content>
          <div class="row">
            <div class="col-12 col-md-6">
              <label><b>Serial</b></label>
              <h5>{{ qualityCheck.serial }}</h5>
              <br>
              <label><b>Timestamp</b></label><br>
              {{ qualityCheck.timestamp | formatTimestamp }}
            </div>
            <div class="col-12 col-md-6">
              <label><b>Status</b></label>
              <div class="d-inline-flex w-100">
                <input class="position-absolute v-hidden" type="radio" name="is_passed" id="is_passed_ok" v-model="qualityCheck.is_passed" :value="true" />
                <label class="w-100 btn btn-large py-3 px-4 mr-1" :class="qualityCheck.is_passed === true ? 'btn-success':'btn-outline-success'" for="is_passed_ok"><h5 class="mb-0">OK</h5></label>
                <input class="position-absolute v-hidden" type="radio" name="is_passed" id="is_passed_ko" v-model="qualityCheck.is_passed" :value="false" />
                <label class="w-100 btn btn-large py-3 px-4 ml-1" :class="qualityCheck.is_passed === false ? 'btn-danger':'btn-outline-danger'"  for="is_passed_ko"><h5 class="mb-0">KO</h5></label>
              </div>
              <br>
              <label><b>Notes</b></label><br>
              <textarea class="form-control" v-model="qualityCheck.notes"></textarea>
            </div>
            <div class="col-12 text-center">
              <button @click="registerQualityCheckResult()" class="btn btn-primary mt-3"><i class="fa fa-save mr-2"></i>Register quality check result</button>
            </div>
          </div>
        </template>
      </Modal>
      <Modal v-if="order.product.production_instructions" title="Istruzioni pre-assemblaggio" extraClass="modal-warning" ref="newOrderModal" :hideCloseButton="true">
        <template #content>
          <div v-if="isLoading">Generazione seriali in corso...</div>
          <div v-else class="d-inline-flex">
            <div>
              <strong>{{ order.product.name }}</strong>
              <br>
              <strong>SKU: </strong><span>{{ order.product.sku }}</span>
            </div>
            <div v-if="order.product" class="ml-3 bullet" :style="`background-color: ${colors[order.product.commercial_code]}`"></div>
          </div>
          <div class="row mt-3">
            <div class="col-12 col-md-6">
              <p><strong>Selezionare ID operatori:</strong></p>
              <ul>
                <template v-for="(id, index) in operators">
                  <li :key="id">
                    <input v-model="order.operators[index]" class="mr-2" :true-value="id" :id="id" type="checkbox"/>
                    <label :for="id">{{ id }}</label>
                  </li>
                </template>
              </ul>
            </div>
            <div class="col-12 col-md-6">
              <p><strong>Approvvigionare le seguenti componenti a bordo linea prima di iniziare l'assemblaggio:</strong></p>
              <ol class="pl-4">
                <template v-for="(pi, index) in order.product.production_instructions.split(';')">
                  <li v-if="pi.length" :key="`pi${index}`">
                    <input v-model="production_instructions_checklist[index]" class="mr-2" :id="`pi${index}`" type="checkbox"/>
                    <label :for="`pi${index}`">{{ pi }}</label>
                  </li>
                </template>
              </ol>
            </div>
          </div>
          <!-- TODO: Color and operators checklist -->
          <button class="btn btn-primary" @click="startProduction()" :disabled="!isLineReady || !hasOperator">Tutto Pronto</button>
        </template>
      </Modal>
      <Modal v-if="isCompleted" title="Ordine completato" extraClass="modal-success" ref="completedOrderModal">
        <template #content>
          <p>
            Ricordati di:
            <ol class="pl-4">
              <li>segnalare qui sotto eventuali componenti o materie prime non conformi</li>
              <li>scaricare la linea di assemblaggio dalle componenti specifiche utilizzate per l'ordine appena terminato</li>
              <li>ripulire la linea da eventuali semilavorati</li>
              <li>nel caso in cui il turno fosse finito, chiudere le scatole delle materie prime a bordo linea</li>
            </ol>
          </p>
          <a href="#nc-components" class="btn btn-danger mr-2" @click="$refs.completedOrderModal.toggle()">Segnala NC</a>
          <button class="btn btn-primary" @click="$refs.completedOrderModal.toggle()">Finito</button>
        </template>
      </Modal>
      <div id="nc-products" class="py-4" v-if="order.nc_products && order.nc_products.length">
        <h6>NC Products</h6>
        <table class="table table-bordered table-responsive table-sm">
          <thead>
            <tr>
              <th>Seleziona</th>
              <th>#</th>
              <th v-for="field in productFields" :key="`f-${field.key}`" :class="{'d-none': field.type === 'AUTO'}">
                {{ field.name }}
              </th>
              <th>Motivazione</th>
              <th>Azione</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(nc, index) in order.nc_products" :key="`ncp-${index}`">
              <td class="text-center">
                <input type="checkbox" v-model="nc.selected" name="ncSelection"/>
              </td>
              <td>
                <div>
                  <strong class="col">{{ index + 1 }}</strong>
                  <small class="text-nowrap">{{ nc.timestamp | formatTimestamp }}</small>
                </div>
              </td>
              <td v-for="field in productFields" :key="`ncp-${index}-${field.key}`" :class="{'d-none': field.type === 'AUTO'}">
                {{ nc[field.key] }}
              </td>
              <td>
                <div class="select">
                  <select class="form-control form-control-sm ml-1" @change="nc.reason = JSON.parse($event.target.value), updateProductionOrder()">
                    <option disabled>Motivazione scarto</option>
                    <optgroup v-for="(c, index) in discardReasons" :key="index+c.category" :label="c.category">
                      <option v-for="(v, index) in c.values" :key="index+v.name" 
                        :value="JSON.stringify({id: v.id, value: v.name, category: c.category, index: c.index})"
                        :selected="JSON.stringify(nc.reason) === JSON.stringify({id: v.id, value: v.name, category: c.category, index: c.index})">
                        {{ v.name }}
                      </option>
                    </optgroup>
                  </select>
                </div>
              </td>
              <td>
                <button 
                  :disabled="isCompleted && $router.user.roleScore <= 2" 
                  class="btn btn-danger" 
                  @click="order.nc_products.splice(index,1), updateProductionOrder()"
                  title="Rimuovi">x</button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="btn-group">
          <button class="btn btn-primary btn-outline-primary" @click="createReworkOrder('maintenance')">Create Maintenance Order</button>
          <button class="btn btn-primary btn-outline-primary" @click="createReworkOrder('rework')">Create Rework Order</button>
        </div>
      </div>
      <hr>
      <div id="nc-components" class="py-4">
        <h6>NC Components</h6>
        <table class="table table-bordered table-responsive table-sm">
          <thead>
            <tr>
              <th>#</th>
              <th>SKU</th>
              <th>Nome</th>
              <th>Quantità</th>
              <th>Motivazione</th>
              <th>Note</th>
              <th>Azione</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(d, index) in order.nc_components" :key="`ncc-${index}`">
              <td>
                <div>
                  <strong class="col">{{ index + 1 }}</strong>
                </div>
              </td>
              <td>
                {{ d.sku }}
              </td>
              <td>
                {{ d.name }}
              </td>
              <td>
                <input type="number" v-model="d.quantity" :disabled="isCompleted && $router.user.roleScore <= 2" min="1" @change="updateProductionOrder(true)" />
              </td>
              <td>
                <span v-if="d.reason">{{ d.reason.value }}</span>
              </td>
              <td>
                <textarea v-model="d.notes" @change="updateProductionOrder(true)"></textarea>
                <input type="text" v-model="d.serial" placeholder="Serial" @change="updateProductionOrder(true)" :disabled="isCompleted && $router.user.roleScore <= 2" />
              </td>
              <td>
                <button :disabled="isCompleted && $router.user.roleScore <= 2" class="btn btn-danger" 
                @click="order.nc_components.splice(index,1), updateProductionOrder(true)"
                title="Rimuovi">x</button>
              </td>
            </tr>
            <tr>
              <td>
                <div>
                  <strong class="col">{{ order.nc_components.length + 1 }}</strong>
                </div>
              </td>
              <td colspan="2">
                <div class="select">
                  <select class="form-control form-control-sm" @click="getComponents()" @change="ncComponent = {...ncComponent, ...JSON.parse($event.target.value)}">
                    <option selected disabled>Seleziona componente</option>
                    <option v-for="(c,index) in components" :key="c.itemCode+index" :value="JSON.stringify({sku: c.itemCode, name:c.itemName})">{{ c.itemCode }} - {{ c.itemName }}</option>
                  </select>
                </div>
              </td>
              <td>
                <input type="number" v-model="ncComponent.quantity" min="1"/>
              </td>
              <td>
                <div class="select">
                  <select class="form-control form-control-sm ml-1" @change="ncComponent.reason = JSON.parse($event.target.value)">
                    <option selected disabled>
                      Seleziona motivazione
                    </option>
                    <optgroup v-for="(c, index) in discardReasons" :key="index+c.category" :label="c.category">
                      <option v-for="(v, index) in c.values" :key="index+v.name"
                        :value="JSON.stringify({id: v.id, value: v.name, category: c.category, index: c.index})">
                        {{ v.name }}
                      </option>
                    </optgroup>
                    <optgroup label="Altro">
                      <option :value="JSON.stringify({id: 0, value: 'Componente non conforme', category: 'Altro', index: 0})">
                        Componente non conforme
                      </option>
                    </optgroup>
                  </select>
                </div>
              </td>
              <td>
                <textarea v-model="ncComponent.notes"></textarea>
                <input type="text" v-model="ncComponent.serial" placeholder="Serial" />
              </td>
              <td>
                <button class="btn btn-primary" @click="order.nc_components.push(ncComponent), ncComponent = { quantity: '1' }, updateProductionOrder(true)" title="Aggiungi">+</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import ProductionService from "@/services/ProductionService";
import SapService from "@/services/SapService";
import downloadCsv from "../utils/downloadCsv";
import productionRules from "../utils/productionRules.json";
import macRevisionList from "../utils/mac_revision_list.json";
import datamatrixList from "../utils/datamatrix.json";
import Modal from "../utils/Modal";
import BOM from "../products/BOM";
export default {
  name: "ProductionOrderDetails",
  data() {
    return {
      datamatrixList: datamatrixList,
      isLoading: false,
      fields: productionRules.fields,
      operators: productionRules.operators,
      colors: productionRules.colors,
      macR2List: macRevisionList.R2,
      id_number: this.$route.params.id_number,
      order: null,
      labelsCsvData: [],
      serialsCheck: [],
      activeSerialIndex: 0,
      enableEditing: false,
      // Performance optimization data
      pendingChanges: new Set(),
      saveTimeout: null,
      lastSaveTime: 0,
      autoSaveInterval: 15000, // Backup auto-save every 15 seconds (primary save is on row completion)
      autoSaveTimer: null,
      // Error handling and retry logic
      retryConfig: {
        maxRetries: 3,
        retryDelay: 1000, // Start with 1 second
        backoffMultiplier: 2
      },
      failedSaves: new Map(), // Track failed saves for retry
      // Input protection
      activeInputField: null,
      lastInputTime: 0,
      inputProtectionDelay: 3000, // Don't auto-save for 3 seconds after last input
      isSaving: false, // Visual indicator for save operations
      recentlySavedRows: new Set(), // Track recently saved rows for visual feedback
      recentlyCompletedRows: new Set(), // Track recently completed rows for different visual feedback
      visualFeedbackTimers: new Set(), // Track visual feedback timers for cleanup
      serialsRegexp: {
        mac: /^[A-Z,0-9]{12}$/,
        techunit: /02([0-9]{8}$)/,
        old_product: /NT([0-9]{7}$)/,
        product: /([0-9]{11,20}$)/,
        product_2: /([0-9]{11}$)/,
        product_3: /([0-9]{11}$)/,
        mac_extended: /^[0-9]{6}[A-Z,0-9]{12}$/,
        power_supply: /^[a-zA-Z]{1}[0-9]{12}$/,
        power_supply_2: /^[A-Z]{1}[0-9]{2}[A-Z]{2}[0-9]{8}$/,
        battery: /^[0-9]{12}$/,
        battery_2: /^[0-9]{8}-[0-9]{3}$/,
        battery_3: /^[Ii][Nn][Bb].*$/,
        filter: /^[A-Z]{2}-[A-Z]{5}[0-9]{2}\/([0-9]{2}[.-][0-9]{2}[.-][0-9]{2,4})$/,
        mainboard: /^[a-zA-Z0-9]{9}.*$/,
        ledboard: /^[a-zA-Z0-9]{9}.*$/,
        // mainboard: /^[a-zA-Z]{4}[0-9]{5}.*$/,
        // ledboard: /^[a-zA-Z]{4}[0-9]{5}.*$/,
        dust_sensor: /([0-9]{6}-[0-9]{2}$)/,
        fan: /^.*$/,
        proximity_sensor: /^.*$/
      },
      production_instructions_checklist: [],
      discardReasons: [],
      disassemblyNotes: [],
      ncProductsSelection: [],
      ncComponent: {
        quantity: '1'
      },
      components: [],
      selectedSerial: false,
      qualityCheck: {
        serial: null,
        timestamp: null,
        is_passed: null,
        notes: null
      }
    };
  },
  components: { downloadCsv, Modal, BOM },
  sockets: {
    async productionOrderUpdated(updatedOrder) {
      console.log(
        "[socket.io] Production order has been updated. " + updatedOrder.id_number
      )
      
      // Skip reload if user is actively typing
      if (this.isUserActivelyTyping()) {
        console.log("[socket.io] Skipping reload - user is actively typing")
        return;
      }
      
      // Only reload if this update came from another user/session
      // Skip reload if we just made this update ourselves
      const timeSinceLastSave = Date.now() - this.lastSaveTime;
      if (timeSinceLastSave > 3000) { // Increased from 2 to 3 seconds
        console.log("[socket.io] Reloading order data from external update")
      await this.getProductionOrder(this.id_number)
      } else {
        console.log("[socket.io] Skipping reload - we just made this update")
      }
    }
  },
  computed:{
    isCompleted(){
      return this.order.status === 'completed'
    },
    isLineReady(){
      return this.production_instructions_checklist.every(c => c)
    },
    hasOperator(){
      return this.order.operators.filter(o => o && o.length).length > 0
    },
    productFields(){
      return Object.keys(this.fields).map((field) => {
          if (this.fields[field] && this.fields[field][this.order.product.commercial_code] !== 'HIDDEN') {
            let isBatch = this.fields[field].batch
            if (this.fields[field].batch_exclude?.includes(this.order.product.commercial_code)) {
              isBatch = false
            }
            // Fields with a batch prefix configured for this SKU are filled in batch only
            if (this.batchPrefixConfig(field)) {
              isBatch = true
            }
            return {
              key: field,
              name: this.fields[field].name,
              type: this.fields[field][this.order.product.commercial_code],
              batch: isBatch
            }
          }
        }).filter( Boolean )
    },
    isReworkOrder(){
      return this.order.typology === 'rework'
    },
    isDisassemblyOrder(){
      return this.order.typology === 'disassembly'
    },
    visibleFieldsCount(){
      return this.productFields.filter(f => f.type !== 'AUTO').length + 3 + (this.isReworkOrder ? 1 : 0)
    },
    product() {
      return this.order.product.commercial_code
    },
    isShelfy(){
      return this.product === 'SHL0' || this.product === 'SHL2'
    },
  },
  async mounted() {
    this.selectedSerial = this.$route.query.serial ?? false
    
    // Load fresh data from server
    await this.getProductionOrder(this.id_number)
    
    if (this.order.status === 'new' && this.order.product.production_instructions && (this.order.typology === 'production' || this.isReworkOrder)) {
      this.$refs.newOrderModal.toggle()
    }
    this.discardReasons = productionRules.discardReasons.reduce((a, c) => {
        const values = c.values.filter((l)=> l.products.includes(this.order.product.commercial_code))
        if (values.length) { a.push({ ...c, values }) }
        return a
      },[])
    if (this.isDisassemblyOrder) {
      this.disassemblyNotes = productionRules.disassemblyNotes[this.order.product.commercial_code]
    }
    this.getComponents()
    
    // Setup auto-save interval with input protection
    this.autoSaveTimer = setInterval(() => {
      if (this.pendingChanges.size > 0 && !this.isUserActivelyTyping()) {
        this.saveOptimizedChanges();
      }
    }, this.autoSaveInterval);
  },
  beforeDestroy() {
    // Clean up timers
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }
    
    // Clean up visual feedback timers
    this.visualFeedbackTimers.forEach(timer => clearTimeout(timer));
    this.visualFeedbackTimers.clear();
    
    // Save final state before leaving (but don't await to prevent blocking navigation)
    if (this.pendingChanges.size > 0) {
      // Fire and forget - user is navigating away
      this.saveOptimizedChanges().catch(error => {
        console.warn('Failed to save final state on component destroy:', error);
      });
    }
  },
  methods: {
    async getProductionOrder(id) {
      await ProductionService.getProductionOrder(id).then((response) => {
        this.order = response.data;
        this.getSerialsCount(this.order.product);
        this.setLabelsCsvData(this.order);
      });
      
      const lineItemsCount = this.order.product.production_instructions.split(';').filter(p => p.length).length
      this.production_instructions_checklist = Array(lineItemsCount).fill(false);
    },
    async startProduction(){
      this.isLoading = !this.isLoading
      this.order.operators = this.order.operators.filter(o => o && o.length);
      await ProductionService.startProductionOrder(this.order.id_number, this.order)
        .then(async() => {
          await this.getProductionOrder(this.order.id_number)
          this.$refs.newOrderModal.toggle()
        })
        .catch(()=>{
          console.log('Errore durante la generazione dei seriali.')
        })
      this.isLoading = !this.isLoading
    },
    getSerialsCount(product) {
      !product.quantity_actual ? product.quantity_actual = product.quantity : ''
      let productsCount = product.quantity_actual
      let n = productsCount - this.order.serials.length;
      
      const requiredFields = {}
      const serialFields = {}
      Object.keys(this.fields).forEach((field) => { 
        if (this.fields[field] && this.fields[field][this.order.product.commercial_code] === 'REQUIRED') { 
          serialFields[field] =  null 
          requiredFields[field] =  null 
        }
        if (this.fields[field] && this.fields[field][this.order.product.commercial_code] === 'AUTO') { 
          serialFields[field] =  null 
        } 
      })

      for (let i = 0; i < n; i++) {
        this.order.serials.push({
          ...requiredFields,
          sku: product.sku
        }); 
      }

      this.serialsCheck = [];
      for (let i = 0; i < productsCount; i++) {
        this.serialsCheck.push({...requiredFields})
        Object.keys(requiredFields).forEach((field) => {
          this.serialsInputController(this.order.serials[i], i, field)
        })
      }
    },
    async serialsInputController(serial, index, field) {
      if (field && (field === 'product' || field === 'product_2' || field === 'product_3' || field === 'ex_serial')) {

        if (serial[field]?.toLowerCase().includes("https://link.vitesy.com/s/")) {
          serial[field] = serial[field]?.toLowerCase().replace("https://link.vitesy.com/s/","")
        }

        const isDuplicate = this.order.serials.some(
          (s, i) => i !== index && s[field] === serial[field]
        );

        if (field === 'product' && serial[field] &&isDuplicate) {
          alert(`Il seriale "${serial[field]}" è già stato registrato.`);
          this.serialsCheck[index][field] = false;
          serial[field] = ""; // Clear the duplicate serial
          return;
        }

        if(this.isDisassemblyOrder && field === 'product' && (this.serialsRegexp.product.test(serial[field]) || this.serialsRegexp.old_product.test(serial[field]))) {
          const response = await this.getProductProductionData(serial[field])
          if (response?.data?.serials) {
            let exSerial = response.data.serials
            delete exSerial.timestamp
            Object.assign(serial, exSerial) 
            this.serialsCheck[index][field] = true
          } else {
            this.serialsCheck[index][field] = false
          }
        }
      }
      if (field && field === 'product_3' && this.serialsRegexp.product_3.test(serial.product_3)) {
        const response = await this.getProductProductionData(serial.product_3)
        if (response?.data?.serials && response.data.product.color === this.order.product.color && response.data.product.plug === this.order.product.plug) {
          serial.mac = response.data.serials.mac
          serial.power_supply = response.data.serials.power_supply
          this.serialsCheck[index].product_3 = true
        } else {
          this.serialsCheck[index].product_3 = false
        }
      } else if (field && field === 'ex_serial' && this.serialsRegexp.product.test(serial.ex_serial)) {
        const response = await this.getProductProductionData(serial.ex_serial)
        // if (response?.data?.serials && response.data.product.color === this.order.product.color && response.data.product.plug === this.order.product.plug) {
        if (response?.data?.serials) {
          let exSerial = response.data.serials
          delete exSerial.product
          delete exSerial.timestamp
          Object.assign(serial, exSerial) 
          this.serialsCheck[index].ex_serial = true
        } else {
          this.serialsCheck[index].ex_serial = false
        }
      } else if (field && field === 'mac') {
        if (!serial.mac || !serial.mac.length) { this.serialsCheck[index].mac = null }
        else if (serial.mac.substr(0,3) === '100'){
          const { mac } = datamatrixList.find(d => d.datamatrix === serial.mac)
          if (mac) {
            serial.mac = mac.toUpperCase().replaceAll(':','')
          }
        }
        else if (this.serialsRegexp.mac_extended.test(serial.mac.toUpperCase())) {
          serial.mac = serial.mac.toUpperCase().substr(6, serial.mac.length)
          this.serialsCheck[index].mac = true
        } else if (this.serialsRegexp.mac.test(serial.mac.toUpperCase())) {
          this.serialsCheck[index].mac = true
        } else {
          this.serialsCheck[index].mac = false 
        }

        // TEMP REV2 FEBREZE CHECK
          if (serial.mac && serial.mac.length && this.order.product.sku === 'FZ00AA00_A') {
            if (this.macR2List.includes(serial.mac)) {
              this.serialsCheck[index].mac = true
            } else {
              this.serialsCheck[index].mac = false
              alert('ATTENZIONE: Questo MAC address non corrisponde ad una scheda Revisione 2 richiesta per questo prodotto!')
            }
          }
      } else if (field && field === 'power_supply') {
        const batchPrefixRegexp = this.batchPrefixRegexp(field)
        serial[field]?.length ? this.serialsRegexp.power_supply.test(serial[field]) || this.serialsRegexp.power_supply_2.test(serial[field]) || (batchPrefixRegexp && batchPrefixRegexp.test(serial[field])) ? (this.serialsCheck[index][field] = true) : (this.serialsCheck[index][field] = false) : (this.serialsCheck[index][field] = null)
      } else if (field && field === 'battery') {
        serial[field]?.length ? this.serialsRegexp.battery.test(serial[field]) || this.serialsRegexp.battery_2.test(serial[field]) || this.serialsRegexp.battery_3.test(serial[field]) ? (this.serialsCheck[index][field] = true) : (this.serialsCheck[index][field] = false) : (this.serialsCheck[index][field] = null)
      } else if (field && (field === 'product')) {
        serial[field]?.length ? (this.serialsRegexp[field].test(serial[field]) || this.serialsRegexp['old_product'].test(serial[field])) && (this.isDisassemblyOrder || this.order.product_serials.includes(serial[field])) ? (this.serialsCheck[index][field] = true) : (this.serialsCheck[index][field] = false) : (this.serialsCheck[index][field] = null)
      } else if (field && field === 'product_2') {
        serial[field]?.length ? this.serialsRegexp[field].test(serial[field]) && this.order.product_2_serials.includes(serial[field]) ? (this.serialsCheck[index][field] = true) : (this.serialsCheck[index][field] = false) : (this.serialsCheck[index][field] = null)
      } else if (field && field === 'fan') {
        if (serial[field]?.toLowerCase().includes("http://sunon.bz/?")) {
          serial[field] = serial[field]?.toLowerCase().replace("http://sunon.bz/?","")
        }
        serial[field]?.length ? this.serialsRegexp[field].test(serial[field]) ? (this.serialsCheck[index][field] = true) : (this.serialsCheck[index][field] = false) : (this.serialsCheck[index][field] = null)
      } else {
        serial[field]?.length ? this.serialsRegexp[field].test(serial[field]) ? (this.serialsCheck[index][field] = true) : (this.serialsCheck[index][field] = false) : (this.serialsCheck[index][field] = null)
      }


    },
    async getProductProductionData(serial){
      const productProductionData = await ProductionService.getProductSerials(serial)
      return productProductionData
    },
    activeSerialIndexCheck(index){
      if (this.activeSerialIndex !== index) {
        const prevIndex = this.activeSerialIndex;
        
        // Prevent race condition by updating index immediately
        this.activeSerialIndex = index;
        
        // Save previous row if it exists and has any data
        if (prevIndex >= 0 && this.order.serials[prevIndex]) {
          const hasAnyData = Object.values(this.order.serials[prevIndex]).some((val) => val && val.length);
          const rowCompleted = Object.values(this.order.serials[prevIndex]).every((val) => val || (val && val.length));
          
          if (hasAnyData) {
            // Set timestamp only if row is completed
            if (rowCompleted && !this.order.serials[prevIndex].timestamp) {
              this.order.serials[prevIndex].timestamp = new Date();
            }
            
            // Mark this row as changed for immediate saving
            this.pendingChanges.add(prevIndex);
            
            // Save the row (completed or partial)
            this.saveRowOnNavigation(rowCompleted);
          }
        }
      }
    },
    async updateProductionOrder(disabledCompletedModal, forceFullUpdate = false) {
      // Use optimized update if we have pending changes, otherwise full update
      if (!forceFullUpdate && this.pendingChanges.size > 0) {
        return this.saveOptimizedChanges(disabledCompletedModal);
      }
      
      this.isLoading = true
      try {
      await ProductionService.updateProductionOrder(
        this.order.id_number,
        this.order
        );
        this.lastSaveTime = Date.now();
        this.pendingChanges.clear();
        
        // Only reload if we need to check completion status
          if (this.isCompleted && !disabledCompletedModal) {
          await this.getProductionOrder(this.id_number);
          this.$refs.completedOrderModal.toggle();
        }
      } catch (error) {
        console.error('Error updating production order:', error);
        this.showError('Failed to save production order. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },
    // ⚡ PERFORMANCE OPTIMIZATION METHODS
    scheduleAutoSave() {
      // Clear existing timeout
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      
      // Don't auto-save if user is actively typing
      const timeSinceLastInput = Date.now() - this.lastInputTime;
      if (timeSinceLastInput < this.inputProtectionDelay) {
        // Reschedule after protection period expires
        this.saveTimeout = setTimeout(() => {
          this.scheduleAutoSave();
        }, this.inputProtectionDelay - timeSinceLastInput + 100);
        return;
      }
      
      // Schedule new save if we haven't saved recently
      const timeSinceLastSave = Date.now() - this.lastSaveTime;
      const delay = Math.max(2000, this.autoSaveInterval - timeSinceLastSave);
      
      this.saveTimeout = setTimeout(() => {
        if (this.pendingChanges.size > 0 && !this.isUserActivelyTyping()) {
          this.saveOptimizedChanges();
        }
      }, delay);
    },
    
    async saveOptimizedChanges(disabledCompletedModal = false, saveType = 'auto_save') {
      if (this.pendingChanges.size === 0) return;
      
      this.isSaving = true;
      
      const changedSerials = Array.from(this.pendingChanges).map(index => ({
        index,
        serial: this.order.serials[index]
      }));
      
      const saveFunction = async () => {
        return ProductionService.updateProductionOrderSerials(
          this.order.id_number,
          changedSerials
        );
      };
      
      const retryKey = `optimized_save_${Date.now()}`;
      const success = await this.saveWithRetry(saveFunction, null, retryKey);
      
      if (success) {
        this.lastSaveTime = Date.now();
        this.pendingChanges.clear();
        
        // Check if order is now completed - but don't reload data to avoid input conflicts
        const isNowCompleted = this.order.serials.length && this.order.serials.every((serial) => {
          return Object.values(serial).every((val) => val || (val && val.length))
        });
        
        if (isNowCompleted && this.order.status !== 'completed' && !disabledCompletedModal) {
          this.order.status = 'completed';
          this.$refs.completedOrderModal.toggle();
        }
        
        const saveTypeLabel = saveType === 'row_completion' ? 'Row Complete' : 
                              saveType === 'row_completion_delayed' ? 'Row Complete (Delayed)' :
                              saveType === 'row_navigation' ? 'Row Navigation' :
                              saveType === 'row_navigation_delayed' ? 'Row Navigation (Delayed)' : 'Auto-save';
        console.log(`[${saveTypeLabel}] Successfully saved ${changedSerials.length} serial changes`);
        
        // Show success feedback for completed rows
        this.showRowSaveSuccess(changedSerials, saveType);
      } else {
        // All retries failed, fallback to full update as last resort
        console.log('All retries failed, falling back to full update...');
        return this.updateProductionOrder(disabledCompletedModal, true);
      }
      
      this.isSaving = false;
    },
    
    markSerialAsChanged(index) {
      this.pendingChanges.add(index);
      this.scheduleAutoSave();
    },
    
    showError(message) {
      // You can replace this with your preferred notification system
      alert(message);
    },
    
    // Visual feedback for saved rows
    showRowSaveSuccess(changedSerials, saveType = 'auto_save') {
      changedSerials.forEach(({ index }) => {
        this.recentlySavedRows.add(index);
        
        // Add special highlight for completed rows
        if (saveType === 'row_completion' || saveType === 'row_completion_delayed') {
          const isRowCompleted = this.order.serials[index] && 
            Object.values(this.order.serials[index]).every((val) => val || (val && val.length));
          
          if (isRowCompleted) {
            this.recentlyCompletedRows.add(index);
            // Remove completed row indicator after 4 seconds
            const completedTimer = setTimeout(() => {
              this.recentlyCompletedRows.delete(index);
              this.visualFeedbackTimers.delete(completedTimer);
            }, 4000);
            this.visualFeedbackTimers.add(completedTimer);
          }
        }
        
        // Remove the general saved indicator after 3 seconds
        const savedTimer = setTimeout(() => {
          this.recentlySavedRows.delete(index);
          this.visualFeedbackTimers.delete(savedTimer);
        }, 3000);
        this.visualFeedbackTimers.add(savedTimer);
      });
    },
    
    // Input protection methods
    isUserActivelyTyping() {
      const timeSinceLastInput = Date.now() - this.lastInputTime;
      return timeSinceLastInput < this.inputProtectionDelay;
    },
    
    onInputFocus(field, index) {
      this.activeInputField = `${index}-${field}`;
      this.lastInputTime = Date.now();
    },
    
    onInputBlur() {
      this.activeInputField = null;
      // Don't immediately clear lastInputTime - keep protection for a bit longer
    },
    
    onInputChange(field, index) {
      this.lastInputTime = Date.now();
      this.markSerialAsChanged(index);
    },
    
    // Save row on navigation (completed or partial)
    async saveRowOnNavigation(isCompleted) {
      // If user is actively typing, wait a moment and then save
      if (this.isUserActivelyTyping()) {
        const saveType = isCompleted ? 'row_completion_delayed' : 'row_navigation_delayed';
        console.log(`[Row Navigation] User still typing, scheduling ${isCompleted ? 'completed' : 'partial'} row save in 1 second...`);
        const delayedSaveTimer = setTimeout(() => {
          if (this.pendingChanges.size > 0) {
            this.saveOptimizedChanges(false, saveType);
          }
          this.visualFeedbackTimers.delete(delayedSaveTimer);
        }, 1000);
        this.visualFeedbackTimers.add(delayedSaveTimer);
        return;
      }
      
      // Immediately save the row
      const saveType = isCompleted ? 'row_completion' : 'row_navigation';
      console.log(`[Row Navigation] Immediately saving ${isCompleted ? 'completed' : 'partial'} row...`);
      if (this.pendingChanges.size > 0) {
        await this.saveOptimizedChanges(false, saveType);
      }
    },
    
    // Save completed row immediately (but with input protection) - DEPRECATED
    async saveCompletedRow() {
      // This method is now replaced by saveRowOnNavigation
      await this.saveRowOnNavigation(true);
    },
    
    // Manual save method that prevents auto-save conflicts
    async manualSave() {
      // Temporarily disable auto-save
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      
      console.log('[Manual Save] Saving production order...');
      
      if (this.pendingChanges.size > 0) {
        await this.saveOptimizedChanges();
      } else {
        await this.updateProductionOrder();
      }
      
      // Re-enable auto-save scheduling
      setTimeout(() => {
        if (this.pendingChanges.size > 0) {
          this.scheduleAutoSave();
        }
      }, 1000);
    },
    
    // Enhanced error handling with retry logic
    async retryFailedSave(saveFunction, saveData, retryKey) {
      const existingRetry = this.failedSaves.get(retryKey) || { attempts: 0 };
      
      if (existingRetry.attempts >= this.retryConfig.maxRetries) {
        this.showError(`Failed to save after ${this.retryConfig.maxRetries} attempts. Please check your connection and try manually saving.`);
        this.failedSaves.delete(retryKey);
        return false;
      }
      
      try {
        const delay = this.retryConfig.retryDelay * Math.pow(this.retryConfig.backoffMultiplier, existingRetry.attempts);
        
        if (existingRetry.attempts > 0) {
          console.log(`Retrying save attempt ${existingRetry.attempts + 1}/${this.retryConfig.maxRetries} in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        await saveFunction(saveData);
        this.failedSaves.delete(retryKey);
        
        if (existingRetry.attempts > 0) {
          console.log(`Save successful after ${existingRetry.attempts + 1} attempts`);
        }
        
        return true;
      } catch (error) {
        existingRetry.attempts++;
        this.failedSaves.set(retryKey, existingRetry);
        
        console.error(`Save attempt ${existingRetry.attempts} failed:`, error);
        
        // Schedule next retry
        if (existingRetry.attempts < this.retryConfig.maxRetries) {
          setTimeout(() => {
            this.retryFailedSave(saveFunction, saveData, retryKey);
          }, this.retryConfig.retryDelay * Math.pow(this.retryConfig.backoffMultiplier, existingRetry.attempts));
        } else {
          this.showError(`Failed to save after ${this.retryConfig.maxRetries} attempts. Data has been preserved locally. Please check your connection and try again.`);
        }
        
        return false;
      }
    },
    
    async saveWithRetry(saveFunction, saveData, retryKey) {
      try {
        await saveFunction(saveData);
        return true;
      } catch (error) {
        console.error('Initial save failed, starting retry logic:', error);
        return this.retryFailedSave(saveFunction, saveData, retryKey);
      }
    },
    
    
    setLabelsCsvData(order) {
      let labelData = []
      if (order.product_serials.length) {
        labelData = order.product_serials.map((serial, index) => {
          return {
            commercial_code: order.product.commercial_code,
            sku: order.product.sku,
            serial: serial,
            batch: order.date.substring(0, 7),
            commercial_name: order.product.commercial_name,
            sku_2: order.product_2 ? order.product_2.sku : order.product.sku,
            serial_2: order.product_2 ? order.product_2_serials[index] : serial,
            color: order.product.color,
            ean: order.product.ean,
            qrcode: `https://link.vitesy.com/s/${serial}`
          }
        })
      }
      this.labelsCsvData = labelData
    },
    batchPrefixConfig(field){
      return this.fields[field]?.batch_prefix?.[this.order?.product?.sku] ?? null
    },
    batchPrefixRegexp(field){
      const config = this.batchPrefixConfig(field)
      if (!config) { return null }
      const escapedPrefix = config.prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return new RegExp(`^${escapedPrefix}[0-9]{${config.digits}}$`)
    },
    batchPrefixScan(field){
      const config = this.batchPrefixConfig(field)
      const fieldName = this.fields[field].name
      const batchDigits = prompt(`Inserisci le ${config.digits} cifre del batch "${fieldName}"`)
      if (!batchDigits) { return }
      if (!new RegExp(`^[0-9]{${config.digits}}$`).test(batchDigits.trim())) {
        alert(`Ops, il batch deve essere composto da ${config.digits} cifre, riprova.`)
        return
      }
      const batchSerial = `${config.prefix}${batchDigits.trim()}`
      const emptyIndexes = this.order.serials.reduce((a, s, i) => {
        if (!s[field] || !s[field].length) { a.push(i) }
        return a
      }, [])
      if (!emptyIndexes.length) {
        alert(`Tutte le righe hanno già un valore nel campo "${fieldName}". Abilita la modifica per cambiarle.`)
        return
      }
      if (!confirm(`Compilare ${emptyIndexes.length} righe con "${batchSerial}"?`)) { return }
      emptyIndexes.forEach((i) => {
        this.$set(this.order.serials[i], field, batchSerial)
        this.serialsInputController(this.order.serials[i], i, field)
        this.pendingChanges.add(i)
      })
    },
    batchScan(field){
      if (this.batchPrefixConfig(field)) { return this.batchPrefixScan(field) }
      let serials = [...this.order.serials]
      let batchQuantity = 0
      const batchSerial = prompt(`Scan the "${field}" batch serial`)
      if (batchSerial){
        const serialOk = this.serialsRegexp[field].test(batchSerial) ? true: false
        if (serialOk) {
          batchQuantity = prompt(`How many "${field}" fields do you want to fill with serial "${batchSerial}"?`)
          const emptyIndex = serials.findIndex((s) => !s[field] || !s[field].length)
          if (batchQuantity && Number(batchQuantity > 0)){
            for (let i = 0; i < batchQuantity; i++) {
              if (!serials[i + emptyIndex]) break
              serials[i + emptyIndex][field] = batchSerial
            }
            this.order.serials = serials
          } else {
            alert('Ops, qualcosa è andato storto, riprova.')
          }
        } else {
          alert('Ops, il codice scansionato non sembra essere corretto, riprova.')
        }
      }
    },
    clearRow(serial){
      for (var key in serial ) {
        delete serial._id
        delete serial.timestamp
        delete serial.active
        if (key !== 'sku') { serial[key] = null }
      }
    },
    discard(serial, index, reasonString) {
      if (confirm('Are you sure you want to discard this product?')){
      const reason = JSON.parse(reasonString)
      if (reason.category === 'Installazione FW' || reason.category === 'Collaudo funzionale') {
        this.order.nc_products.push({...serial, reason: reason, timestamp: new Date(), quantity: 1 })
        this.order.serials.splice(index, 1)
        this.order.product.quantity_actual--
      } else {
        const ncComponent = this.getComponentByDiscardReason(reason.id)
        this.order.nc_components.push({
          sku: ncComponent.itemCode,
          name: ncComponent.itemName,
          quantity: 1,
          reason: reason,
          notes: '',
          serial: reason.value === 'Alimentatore - componente non conforme' ? serial.power_supply : '' 
        })
        reason.category !== 'TAMU' ? this.clearRow(serial) : ''
      }
      this.updateProductionOrder()
      }
    },
    getComponentByDiscardReason(id){
      return this.components.find(c => c.discardReasonId === id)
    },
    async getComponents(){
      if (!this.components.length) {
        const bom = productionRules.bom.filter(p => p.skus.includes(this.order.product.sku))[0]?.items ?? []
        if (bom.length) {
          this.components = bom
        } else {
          // const response = await SapService.getProducts({semilavorato_da_non_esplodere: 'Y'})
          // this.components = response.data
          const response = await SapService.getProducts({itemGroup: 'Materie prime'})
          this.components = response.data.filter(c => c.productFamily === this.order.product.family)
        }
      }
    },
    async createReworkOrder(typology){
      const serials = this.order.nc_products.filter(p => p.selected)
      if (!serials.length) {
        alert('Select at least 1 NC product to create a maintenance order.')
      } else {
        const ncProductsComponents = this.order.nc_products.map((nc) => {
          const ncComponent = this.getComponentByDiscardReason(nc.reason.id)
          if (ncComponent) {
            return {
              sku: ncComponent.itemCode,
              name: ncComponent.itemName,
              quantity: 1,
              reason: nc.reason,
              notes: '',
              serial: nc.product
            }
          }
        }).filter(n => n !== undefined)
        const newIdNumber = `${this.order.id_number}_${typology === 'maintenance' ? 'MT' : 'RW'}`
        const newOrder = {
          id_number: newIdNumber,
          product: {...this.order.product, quantity: serials.length, quantity_actual: serials.length },
          serials: serials,
          typology: typology,
          nc_components: ncProductsComponents
        }
        await ProductionService.addProductionOrder(newOrder)
        this.$router.push(newIdNumber)
        this.$router.go()
      }
    },
    copySerialsToClipboard(){
      const serialsList = this.order.serials.map(s => s.product)
      this.copyToClipboard(serialsList.join('\t'))
    },
    startQualityCheck(serial){
      this.qualityCheck.serial = serial
      this.qualityCheck.timestamp = new Date()
      this.$refs.qualityCheck.toggle()
    },
    async registerQualityCheckResult(){
      const serialIndex = this.order.serials.findIndex(s => s.product === this.qualityCheck.serial)
      if (serialIndex >= 0) {
        this.order.serials[serialIndex].quality_check = this.qualityCheck
        await this.updateProductionOrder(true)
        this.qualityCheck = {
          serial: null,
          timestamp: null,
          is_passed: null,
          notes: null
        }
        this.$refs.qualityCheck.toggle()
      } else {
        alert('Serial not found.')
      }
    },
    showQualityCheckNotes(notes) {
      if(notes && notes.length) {
        alert(`Quality Check Notes\n\n${notes}`)
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import "../../styles/variables.scss";
.details > div > div {
  margin-bottom: 0.5rem;
}
.notes {
  border: 0.2rem solid $red;
  background-color: $ups;
  padding: 0.5rem;
  margin-top: 1rem;
}
.status {
  text-transform: capitalize;
  border-radius: 1.5rem;
  padding: 0.2rem 0.5rem;
  &.completed {
    background-color: $green;
    color: #fff;
  }
  &.in-progress {
    background-color: $ups;
  }
  &.cancelled {
    background-color: $red;
    color: #fff;
  }
  &.new {
    background-color: $clairy;
    color: #fff;
  }
}
.serials {
  &.selected {
    background: $clairy;
  }
  .pattern {
    font-size: 0.7rem;
    color: rgba(#fff, 0.2);
  }
  margin: 0;
  text-align: center;
  align-items: center;
  padding-right: 0.5rem;
  &__heading {
    background-color: $blue;
    color: #fff;
    padding-right: 0;
    margin-bottom: 1rem;
    > div {
      border-right: 1px solid rgba(#fff, 0.2);
    }
    label {
      margin: 0;
      padding: 0.5rem;
    }
  }
  input {
    margin: 0.25rem 0;
    border-left: 0.25rem solid #000;
    &.ok {
      border-color: $green;
    }
    &.error {
      border-color: $red;
    }
  }
}
.product {
  margin-top: 1rem;
  width: 100%;
  tr {
    &._id {
      display: none;
    }
    &.production_instructions { display: none; font-weight: 700; background-color: $ups; }
    td {
      padding: 0.2rem 0.5rem;
      border: 1px solid rgba(0, 0, 0, 0.1);
      b {
        text-transform: capitalize;
      }
    }
  }
}
.print {
  color: $blue;
  border: 1px solid $blue;
  padding: 1.5rem 3rem;
  box-shadow: 0 0 0 0.5rem #fff;
  width: calc(100% - 2rem);
  margin: 1rem;
  &:hover {
    color: #fff;
    background-color: $blue;
    border-color: #fff;
    box-shadow: 0 0 0 0.5rem $blue;
  }
  i {
    //display: block;
    margin: 0.2rem;
    margin-bottom: 1rem;
  }
}
ul {
  columns: 2;
  .strike {
    text-decoration: line-through;
  }
}
input {
  padding: 0.2rem.5rem;
  border: 1px solid rgba(0, 0, 0, 0.2);
  transition: background-color 0.5s;
  &:read-only {
    background-color: rgba($green, 0.5);
  }
  &:disabled {
    background-color: #f0f0f0;
  }
}
.grid_row {
  display: grid;
}
.thead {
  height: 100%;
  display: flex;
  justify-content: center;
  font-size: .75rem;
  &.batch {
    background-color: #343a3f;
  }
}

select.select-danger {
  border-color: $red;
  color: $red;
}


/* Loading state optimization */
.loading {
  pointer-events: none;
  opacity: 0.7;
}

/* Recently saved row feedback */
.serials.grid_row.recently-saved {
  background-color: rgba(40, 167, 69, 0.15);
  border-left: 3px solid #28a745;
  animation: fadeInOut 3s ease-in-out;
}

/* Recently completed row feedback (stronger visual) */
.serials.grid_row.recently-completed {
  background-color: rgba(40, 167, 69, 0.25);
  border-left: 4px solid #28a745;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
  animation: completeFadeInOut 4s ease-in-out;
}

@keyframes fadeInOut {
  0% { background-color: rgba(40, 167, 69, 0.3); }
  50% { background-color: rgba(40, 167, 69, 0.2); }
  100% { background-color: rgba(40, 167, 69, 0.05); }
}

@keyframes completeFadeInOut {
  0% { 
    background-color: rgba(40, 167, 69, 0.4);
    box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  }
  30% { 
    background-color: rgba(40, 167, 69, 0.3);
    box-shadow: 0 3px 6px rgba(40, 167, 69, 0.25);
  }
  70% { 
    background-color: rgba(40, 167, 69, 0.2);
    box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
  }
  100% { 
    background-color: rgba(40, 167, 69, 0.05);
    box-shadow: none;
  }
}
</style>
